import type React from "react";

import { useEffect, useRef } from "react";

interface WebKitGestureEvent extends UIEvent {
  clientX: number;
  clientY: number;
  scale: number;
}

const WHEEL_ZOOM_FACTOR = 1.05;

export const clampZoom = (value: number, maxZoom: number): number =>
  Math.min(maxZoom, Math.max(1, value));

export function usePdfWheelZoom({
  baseWidth,
  contentRef,
  maxZoom,
  onZoomChange,
  scrollerRef,
  zoom,
}: {
  baseWidth?: null | number;
  contentRef: React.RefObject<HTMLElement | null>;
  maxZoom: number;
  onZoomChange?: (zoom: number) => void;
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  zoom: number;
}): void {
  const baseWidthRef = useRef(baseWidth);
  const zoomRef = useRef(zoom);
  const onZoomChangeRef = useRef(onZoomChange);

  baseWidthRef.current = baseWidth;
  zoomRef.current = zoom;
  onZoomChangeRef.current = onZoomChange;

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) return;

    const applyZoomAt = (
      nextZoom: number,
      clientX: number,
      clientY: number,
    ) => {
      const content = contentRef.current;
      const base = baseWidthRef.current ?? scroller.clientWidth;

      if (!content || !base) return;

      const next = clampZoom(nextZoom, maxZoom);

      if (next === zoomRef.current) return;

      const rect = content.getBoundingClientRect();
      const normX = rect.width
        ? Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
        : 0.5;
      const normY = rect.height
        ? Math.min(1, Math.max(0, (clientY - rect.top) / rect.height))
        : 0.5;
      const previousWidth = content.clientWidth;
      const previousHeight = content.clientHeight;

      content.style.width = `${base * next}px`;
      scroller.scrollLeft += normX * (content.clientWidth - previousWidth);
      scroller.scrollTop += normY * (content.clientHeight - previousHeight);

      zoomRef.current = next;
      onZoomChangeRef.current?.(next);
    };

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;

      event.preventDefault();

      const factor =
        event.deltaY < 0 ? WHEEL_ZOOM_FACTOR : 1 / WHEEL_ZOOM_FACTOR;

      applyZoomAt(zoomRef.current * factor, event.clientX, event.clientY);
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });

    let active = false;
    let startZoom = 1;

    const onGestureStart = (event: Event) => {
      event.preventDefault();

      if (active) return;

      active = true;
      startZoom = zoomRef.current;
    };

    const onGestureChange = (event: Event) => {
      event.preventDefault();

      if (!active) return;

      const gesture = event as WebKitGestureEvent;

      applyZoomAt(startZoom * gesture.scale, gesture.clientX, gesture.clientY);
    };

    const onGestureEnd = () => {
      active = false;
    };

    const supportsGestures = "ongesturestart" in window;

    if (supportsGestures) {
      scroller.addEventListener("gesturestart", onGestureStart, {
        passive: false,
      });
      scroller.addEventListener("gesturechange", onGestureChange, {
        passive: false,
      });
      scroller.addEventListener("gestureend", onGestureEnd);
    }

    return () => {
      scroller.removeEventListener("wheel", onWheel);

      if (supportsGestures) {
        scroller.removeEventListener("gesturestart", onGestureStart);
        scroller.removeEventListener("gesturechange", onGestureChange);
        scroller.removeEventListener("gestureend", onGestureEnd);
      }
    };
  }, [contentRef, maxZoom, scrollerRef]);
}
